import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, RotateCcw, Palette } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function SuperadminPartnerLandings() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    api.get(`/admin/partner-landings?${params.toString()}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const reset = async (slug) => {
    if (!window.confirm(`¿Resetear la landing de "${slug}" al template default?`)) return;
    try {
      await api.post(`/admin/partner-landings/${slug}/reset`);
      toast.success(`Landing de ${slug} reseteada`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold flex items-center gap-1">
          <Palette className="w-3.5 h-3.5" /> Landings de partners
        </p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">
          Partner landings
        </h1>
        <p className="mt-2 text-gray-400">
          Listado de todas las landings white-label creadas por partners. Puedes ver el preview público
          y resetear al template default si un partner necesita empezar de cero.
        </p>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <select
            value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="landings-status-filter"
            className="bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]"
          >
            <option value="all">Todos los estados</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>

        <div className="mt-6 overflow-x-auto border border-gray-800 rounded-xl" data-testid="admin-landings-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-4 py-3">Tenant</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Versión</th>
                <th className="text-left px-4 py-3">Última publicación</th>
                <th className="text-left px-4 py-3">Última actualización</th>
                <th className="text-right px-4 py-3">Bloques</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Sin landings.</td></tr>
              ) : items.map((l) => (
                <tr key={l.id} className="border-t border-gray-800" data-testid={`admin-landing-row-${l.tenant_slug}`}>
                  <td className="px-4 py-3">
                    <p className="text-white font-medium">{l.tenant_name || l.tenant_slug}</p>
                    <p className="text-[11px] text-gray-500 font-mono">{l.tenant_slug}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold border ${
                      l.status === "published"
                        ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                        : "bg-amber-500/10 text-amber-300 border-amber-500/30"
                    }`}>
                      {l.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-white">v{l.version}</td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{fmtDate(l.published_at)}</td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{fmtDate(l.updated_at)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-300">{l.blocks_count}</td>
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                    {l.has_published_version && (
                      <Link
                        to={`/l/${l.tenant_slug}`}
                        target="_blank"
                        data-testid={`landing-view-public-${l.tenant_slug}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-gray-700 text-gray-200 hover:bg-white/5 text-xs font-semibold"
                      >
                        <ExternalLink className="w-3 h-3" /> Ver
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => reset(l.tenant_slug)}
                      data-testid={`landing-reset-${l.tenant_slug}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 text-xs font-semibold"
                    >
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-[11px] text-gray-500">
          TODO Fase futura: dominios custom (ej. <span className="font-mono">academia.bancoalfa.mx</span>),
          subdominios y campañas email.
        </p>
      </div>
    </StaffLayout>
  );
}
